import { Injectable } from "@angular/core";
import {
    Observable, of
} from "rxjs";
import { IAutocompleteController } from "../../interfaces/autocomplete-controller";
import { IAutocompleteResults } from "../../interfaces/autocomplete-results";
import { IAutocompleteOption } from "../../interfaces/autocomplete-option";

/**
 * Autocomplete controller service for free-text string values with no
 * canonical suggestion source (e.g. UPCs, validation exclusions, output
 * filters).
 *
 * This service implements the IAutocompleteController interface but always
 * returns an empty options list, since there is no backing entity or API to
 * suggest values from. It is meant to be paired with [allowAnyEntry]="true"
 * on mgd-generic-autocomplete so users can still type and chip arbitrary
 * free-text values.
 *
 * Provided in root as a singleton, shared across any feature that needs a
 * trivial free-text autocomplete controller.
 */
@Injectable({
    providedIn: "root"
})
export class StringAutocompleteControllerService implements IAutocompleteController<string> {

    /**
     * Always resolves an empty options list, since free-text values have no
     * canonical suggestion source.
     *
     * @param query - The search query string (unused)
     * @param options - Additional autocomplete options (unused)
     * @returns Observable of empty autocomplete results
     */
    autocomplete$(query?: string, options?: any): Observable<IAutocompleteResults<string>> {
        return of({
            options: []
        });
    }

    /**
     * Gets the display name for a free-text value.
     * For free-text strings, the value and display name are the same.
     *
     * @param value - The free-text value to display
     * @returns The display name for the value, or an empty string if undefined
     */
    getAutocompleteDisplayName(value?: string): string {
        return value || "";
    }

    /**
     * Gets the identifier for tracking purposes in autocomplete lists.
     * Uses the string value as the unique identifier.
     *
     * @param index - The index of the item in the list
     * @param result - The autocomplete option result
     * @returns The unique identifier for the value
     */
    trackByIdentifier(index: number, result?: IAutocompleteOption<string>): string | undefined {
        return result?.value;
    }

    /**
     * Determines if the value is unique compared to the values in the array
     *
     * @param value - The value to check
     * @param items - Array of existing values
     * @returns True if the value is not in the items array, false otherwise
     */
    isUnique(value: string, items: Array<string>): boolean {
        return !items?.includes(value);
    }
}
